import {cdnImage, cdnUrl} from '@/util/cdnImage';
import {MUIBox, MUIButton, MUIGrid, MUITypography} from '@/components/MUI-Components';
import {EVERLEDGER_META_DATA} from '@/util/metaData/caseStudies';
import Container from '@mui/material/Container';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import {fontSize, fontWeight, spacing} from '@/styles/tokens';
const DiamondGraphic = cdnImage('everledger-images/diamond-graphic.webp');
const digiSign = cdnImage('everledger-images/diamond.webp');
const Everledger1 = cdnImage('everledger-images/everledger-img1.webp');

// ─── sx objects — module-level constants ──────────────────────────────────────
// Classes shared with already-migrated sibling pages (integraledger-heading,
// -kick-heading, -value-color, -study-btn, -adv, -book-heading, -grid-text,
// -result-para) stay as classNames — still consumed by case-study-p2b /
// case-study-ndax-ecosystem / case-study-lummid / case-study-integraledger. This
// page's own overrides of those shared rules (previously nested under
// `.everledger-main-section`, a wrapper exclusive to this page) are now applied as sx
// alongside the kept className, so the root wrapper class is no longer needed and has
// been dropped. Nested selectors like `'& .plant-img'` reach into CaseStudyBanner/
// BostonCoffee's internal DOM the same way the old descendant selectors did — no
// component API changes required.
const everledgerBannerWrapperSx = {
    backgroundImage: `url('${cdnUrl('everledger-images/everledger-bannerimg.webp')}')`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    paddingTop: '30px'
} as const;

// !important required on span color: this sx competes with the shared
// `.casestudy-banner-img span` rule still hardcoded in CaseStudyBanner (globals.scss) —
// same specificity (single class + span), neither side had a source-order edge before, so
// without !important the winner depends on stylesheet insertion order instead of intent.
const everledgerBannerHeadingSx = {
    fontFamily: "'Headers', sans-serif",
    fontSize: '55px',
    fontWeight: fontWeight.bold,
    color: 'RGB(255, 255, 255)',
    lineHeight: '70px',
    maxWidth: '450px',
    '& span': {color: '#75dcff !important'},
    '@media (max-width:430px)': {fontSize: fontSize[30], lineHeight: '45px'}
} as const;

// !important required: competes with the shared `.banner-div` base background-color
// (globals.scss, hardcoded on CaseStudyBanner) at equal specificity and neither side has
// !important on the base rule — a tie that source order was deciding wrong.
const everledgerBannerBackgroundSx = {
    backgroundColor: '#273a4b !important',
    '& .plant-img': {right: '25px', paddingTop: '0px', '& img': {width: 'unset', height: 'unset'}}
} as const;

const everledgerContentBackgroundSx = {
    backgroundColor: '#273a4b',
    '& .plant-img': {right: '25px', paddingTop: '50px', '& img': {width: 'unset', height: 'unset'}},
    '& .coffee-bean-img': {left: '25px', paddingTop: '50px', '& img': {width: 'unset', height: 'unset'}}
} as const;

// Merged with the kept `integraledger-heading` className on Challenge/Perfectblockchain/
// Result/Help — overrides just the span color and the mobile font size for this page.
// !important required on span color: competes with the shared `.integraledger-heading span`
// / `.challenge-heading span` rules (globals.scss), neither of which carries !important —
// same specificity tie that was previously resolved by source order, not by this page's intent.
const everledgerHeadingSx = {
    '& span': {color: '#273a4b !important'},
    '@media (max-width:430px)': {fontSize: fontSize[30], lineHeight: '45px'}
} as const;

// font-size intentionally omitted: the original rule declared `font-size: 26` (no unit),
// an invalid CSS value browsers silently ignore — replicating it with a valid px value
// would be a behavior change, not a faithful migration.
const everledgerChallengeListSx = {
    '& .challenge-list-wrapper': {listStyle: 'none', textAlign: 'center'},
    '& .challenge-list span': {color: '#273a4b', fontFamily: "'Headers', sans-serif"}
} as const;

// Same value as integraledgerChallengeSubheadingSx in case-study-integraledger/index.tsx —
// .integraledger-challenge-subheading is shared unchanged between both pages, no override.
const everledgerChallengeSubheadingSx = {
    fontSize: fontSize[24],
    '& span': {color: '#152c7c', fontWeight: fontWeight.bold}
} as const;

// !important required on backgroundColor: competes with the shared `.ifinca-coffee-wrapper`
// base background-color (globals.scss, hardcoded on BostonCoffee) at equal specificity, no
// !important on the base rule either — a tie source order was deciding wrong. The nested
// `.blockchain-para span` color override needs no change: it compiles to a 2-class descendant
// selector (this box's generated class + .blockchain-para), which already outranks the
// global single-class `.blockchain-para span` rule (also not !important) on specificity alone.
const everledgerBackgroundSx = {
    backgroundColor: '#273a4b !important',
    '& .blockchain-para span': {color: '#273a4b'}
} as const;

const everledgerResultImgBackgroundSx = {
    backgroundColor: '#273a4b',
    position: 'relative',
    '@media (max-width:768px)': {padding: '20px 0'}
} as const;

const everledgerGridTextSx = {
    right: '-130px',
    '@media (max-width:1024px)': {fontSize: '55px !important', right: '-103px'},
    '@media (max-width:768px)': {fontSize: '45px !important', right: '-65px'}
} as const;

const everledgerResultImageSx = {'@media (max-width:768px)': {width: '400px'}} as const;

// `&&` self-nest required: the shared `.integraledger-study-btn` base class (globals.scss,
// hardcoded on MUIButton via className) already declares its background-color !important —
// a true tie that a matching !important on this sx can't win by specificity alone. Doubling
// the generated selector (0,2,0) beats the base class's (0,1,0) outright, independent of
// stylesheet load order.
const everledgerStudyBtnSx = {
    '&&': {
        backgroundColor: '#fff !important',
        color: '#000000 !important'
    }
} as const;

const everledgerBookHeadingSx = {
    maxWidth: '837px',
    '@media (max-width:430px)': {fontSize: fontSize[30], lineHeight: '45px'}
} as const;

const everledgerAdvSx = {
    backgroundImage: `url('${cdnUrl('everledger-images/short-banner.webp')}')`,
    '@media (max-width:430px)': {paddingLeft: 0, paddingRight: 0}
} as const;

const everledgerPerfectMainBoxSx = {position: 'relative', paddingBottom: spacing[40]} as const;

const everledgerPerfectMainTestSx = {'@media (max-width:1024px)': {'& img': {width: '100%', height: '100%'}}} as const;

const everledgerPerfectBackgroundSx = {
    backgroundColor: '#f7f7f7',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    height: '80%',
    zIndex: -1,
    '@media (max-width:768px)': {height: '90%'}
} as const;

const everledgerHelpWrapperSx = {
    backgroundColor: '#f7f7f7',
    '& .help-main-box': {padding: '60px 0'}
} as const;

const everledgerBannerContentSx = {
    fontFamily: "'RegularText', sans-serif",
    fontSize: fontSize[24],
    fontWeight: fontWeight.regular,
    color: 'RGB(255, 255, 255)',
    paddingTop: '25px',
    lineHeight: '36px',
    '& span': {color: '#75dcff', fontFamily: "'Headers', sans-serif", fontWeight: fontWeight.bold},
    '@media (max-width: 430px)': {fontSize: fontSize[18], textAlign: 'left', lineHeight: '30px', '& span': {fontSize: fontSize[18]}}
} as const;

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        everledgerBannerContent,
        everledgerBannerStackData,
        everledgerChallengeList,
        everledgerHelpData,
        everledgerValueDelivered,
        helpData
    } = await import('@/util/contents/everledger-case-study');

    return {
        props: {
            everledgerBannerContent,
            everledgerBannerStackData,
            everledgerChallengeList,
            everledgerHelpData,
            everledgerValueDelivered,
            helpData
        }
    };
};

const CaseStudyBanner = dynamic(import('@/components/Ui-components/casestudy-banner'));
const Challenge = dynamic(import('@/components/Ui-components/ifinca-challenge'));
const Perfectblockchain = dynamic(import('@/components/Ui-components/perfectblockchain'));
const Help = dynamic(import('@/components/Ui-components/havehelp'));
const BostonCoffee = dynamic(import('@/components/Ui-components/ifincabostoncoffee'));
const DisplayImage = dynamic(import('@/components/Ui-components/display-image'));
const Result = dynamic(import('@/components/Ui-components/result'));

const bannerHeading = (
    <>
        A Blockchain- Based Product <span>Authentication Solution</span>
    </>
);
const serviceType = (
    <>
        Luxury Goods <br />
        Supply Chain
    </>
);
const heading = (
    <>
        Challenge - Fostering A Blockchain-Powered <span>Luxury Goods Supply Chain </span>
    </>
);
const righttechheading = (
    <>
        Finding the Perfect Team: <span>Pioneering Innovation Together</span>
    </>
);
const coffeeHeading = (
    <>
        Everledger: Transitioning from <br /> Legacy Migration to
        <span> Full-time In-house Integration</span>
    </>
);
const subheadingtwo = (
    <>
        However, as time progressed and our collaboration flourished, Everledger extended a full-time partnership invitation. We established
        direct communication with their Indian team to exchange updates and receive valuable feedback. <br /> <br />
        Following the successful transformational journey that spanned a couple of years, Everledger decided to establish its team in
        Mumbai. This milestone marked the culmination of our impactful partnership, which resulted in Everledger achieving progressive
        growth and self-sufficiency in driving technological advancements within their organization.
    </>
);
const resultheading = (
    <>
        Result-<span> Seed Funding, Series A round led</span>by Tencent
    </>
);
const resultContent = (
    <>
        Everledger, an innovative blockchain-enabled solution for the meticulous tracking and tracing of luxury goods, achieved a remarkable
        milestone with a successful seed funding round of <span> $120k in March 2015</span>. Building upon this initial success, the company
        secured an impressive<span> $20 million</span> in funding from Tencent, a prominent player in the industry, further solidifying
        Everledger&apos;s position in the market.
    </>
);
const resultContenttwo = (
    <>
        With a subsequent series A round generating <span>$10 million in March 2018</span>, the total funding raised now stands at an
        impressive <span>$30.4 million</span>. This influx of capital will empower Everledger to propel its mission of fostering greater
        sustainability within global supply chains. While initially focused on diamond tracking, Everledger seamlessly diversified into
        various luxury verticals, including wines and precious metals.
    </>
);
const helperheading = (
    <>
        Do you envision a <span> similar business plan?</span>Let&apos;s talk about it ahead
    </>
);

const Everledger = (props: any) => {
    const {
        openForm,
        everledgerBannerContent,
        everledgerBannerStackData,
        everledgerChallengeList,
        everledgerHelpData,
        everledgerValueDelivered,
        helpData
    } = props;
    return (
        <MUIBox>
            <PageHead meta={EVERLEDGER_META_DATA} url="/case-study/everledger" />
            <MUIBox sx={everledgerBannerWrapperSx}>
                <Container maxWidth="lg">
                    <CaseStudyBanner
                        techstack={everledgerBannerStackData}
                        product="Everledger"
                        service={serviceType}
                        heading={bannerHeading}
                        plantimg={digiSign}
                        plantSize="digi-sign"
                        headingSx={everledgerBannerHeadingSx}
                        backgroundSx={everledgerBannerBackgroundSx}
                    />
                </Container>
            </MUIBox>
            <MUIBox className="bottom-spacing" sx={everledgerContentBackgroundSx}>
                <Container maxWidth="lg">
                    <MUIBox className="plant-img digi-sign-img">
                        <DisplayImage src={DiamondGraphic} alt="diamond-graphic" />
                    </MUIBox>
                    <MUIBox className="coffee-bean-img digi-sign-img">
                        <DisplayImage src={digiSign} alt="Everledger diamond gemstone graphic" />
                    </MUIBox>
                    <MUIBox>
                        {everledgerBannerContent?.map((data: any, index: number) => (
                            <MUIBox key={index}>
                                <MUITypography sx={everledgerBannerContentSx}>{data}</MUITypography>
                            </MUIBox>
                        ))}
                    </MUIBox>
                </Container>
            </MUIBox>
            <MUIBox sx={everledgerChallengeListSx}>
                <Container maxWidth="lg">
                    <Challenge
                        heading={heading}
                        subheading={''}
                        challengelistData={everledgerChallengeList}
                        className="integraledger-heading"
                        subheadingSx={everledgerChallengeSubheadingSx}
                        headingSx={everledgerHeadingSx}
                    />
                </Container>
            </MUIBox>
            <MUIBox sx={everledgerPerfectMainBoxSx}>
                <Container maxWidth="lg">
                    <MUIBox sx={everledgerPerfectMainTestSx}>
                        <DisplayImage
                            src={Everledger1}
                            alt="client"
                            style={{width: '100%', height: 'auto'}}
                            sizes="(max-width: 1200px) 100vw, 1200px"
                        />
                    </MUIBox>
                    <MUIBox sx={everledgerPerfectBackgroundSx}></MUIBox>
                    <Perfectblockchain
                        heading={righttechheading}
                        perfectBlockchainData={everledgerHelpData}
                        className="integraledger-heading"
                        headingSx={everledgerHeadingSx}
                    />
                </Container>
            </MUIBox>
            <MUIBox className="margin">
                <BostonCoffee
                    headerimg={cdnImage('everledger-images/everledger-img2.webp')}
                    heading={coffeeHeading}
                    subheading="Everledger engaged our services to facilitate their migration from an outdated version of Hyperledger Fabric to the latest iteration. Initially, we assigned a dedicated team of skilled developers to undertake the project. "
                    subheadingtwo={subheadingtwo}
                    contentheading="Value Delivered"
                    valuedelivered={everledgerValueDelivered}
                    bostonimg={digiSign}
                    // bottomimg={digiSign}
                    headingclass="integraledger-kick-heading"
                    contentheadingclass="integraledger-value-color"
                    bottomimgclass="sign-bottom-img"
                    backgroundSx={everledgerBackgroundSx}
                />
            </MUIBox>
            <MUIBox>
                <Container maxWidth="lg">
                    <Result
                        heading={resultheading}
                        resultcontent={resultContent}
                        resultcontenttwo={resultContenttwo}
                        gridimg={cdnImage('everledger-images/Everledger.webp')}
                        gridsidetext="Everledger"
                        className="integraledger-heading"
                        resultcontentclass="integraledger-result-para"
                        plantleft={digiSign}
                        gridtextclass="integraledger-grid-text"
                        friendsimgclass="everledger-result-img"
                        headingSx={everledgerHeadingSx}
                        gridImgSx={everledgerResultImgBackgroundSx}
                        gridTextSx={everledgerGridTextSx}
                        friendImageSx={everledgerResultImageSx}
                    />
                </Container>
            </MUIBox>
            <MUIBox sx={everledgerHelpWrapperSx}>
                <Container maxWidth="lg">
                    <Help heading={helperheading} helpdata={helpData} className="integraledger-heading" headingSx={everledgerHeadingSx} />
                </Container>
            </MUIBox>
            <MUIBox className="integraledger-adv bottom-space" sx={everledgerAdvSx}>
                <Container maxWidth="lg">
                    <MUITypography className="integraledger-book-heading" sx={everledgerBookHeadingSx}>
                        Are you seeking guidance in integrating{' '}
                        <span> blockchain technology into your business or exploring innovative ideas</span> to revolutionize the business
                        landscape?
                    </MUITypography>
                    <MUIGrid container>
                        <MUIBox className="spectrum-btn">
                            <MUIButton
                                label="Book Free Consultation"
                                className="integraledger-study-btn"
                                onClick={openForm}
                                sx={everledgerStudyBtnSx}
                            />
                        </MUIBox>
                    </MUIGrid>
                </Container>
            </MUIBox>
        </MUIBox>
    );
};

export default Everledger;
Everledger.Layout = 'Main';
